// Radha — language picker for the header.
//
// The tenant's locale (063) is the default; this is a personal override stored
// in the browser, so a Dubai finance team can work in English while the tenant
// stays ar-AE for customer-facing output. "Use tenant default" clears it.
import React, { useState } from "react";

import {
  AVAILABLE_LOCALES, LOCALE_LABEL, getLocale, setLocale, clearLocaleOverride,
  hasOverride, t, type Locale,
} from "../lib/i18n";

export function LocaleSwitcher({ tenantTag, onChange }: { tenantTag: string | null; onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const active = getLocale();

  const pick = (l: Locale) => {
    // Keep the tenant's region when the language matches it (ar + ar-AE ->
    // ar-AE), so currency/date formatting stays regionally correct.
    const region = tenantTag && tenantTag.split("-")[0] === l ? tenantTag : undefined;
    setLocale(l, region);
    setOpen(false);
    onChange();
  };

  const useDefault = () => { clearLocaleOverride(tenantTag); setOpen(false); onChange(); };

  return (
    <span style={{ position: "relative" }}>
      <button onClick={() => setOpen((v) => !v)} className="sanctuary-btn"
        title={t("app.language")} aria-label={t("app.language")} aria-expanded={open}
        style={{
          border: "1px solid var(--border)", borderRadius: 9, padding: "6px 10px",
          background: "var(--surface)", color: "var(--fg-muted)", cursor: "pointer",
          fontSize: 12, letterSpacing: ".04em", textTransform: "uppercase",
        }}>
        {active}
      </button>
      {open && (
        <div role="menu" style={{
          position: "absolute", insetInlineEnd: 0, top: "calc(100% + 6px)", zIndex: 50,
          background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10,
          boxShadow: "var(--glow-shadow)", padding: 5, minWidth: 172,
        }}>
          {AVAILABLE_LOCALES.map((l) => (
            <button key={l} role="menuitem" onClick={() => pick(l)}
              style={{
                display: "block", width: "100%", textAlign: "start", padding: "7px 10px",
                border: 0, borderRadius: 7, cursor: "pointer", fontSize: 13,
                background: l === active ? "var(--surface-2)" : "none",
                color: l === active ? "var(--fg)" : "var(--fg-muted)",
                fontWeight: l === active ? 600 : 400,
              }}>
              {LOCALE_LABEL[l]}
            </button>
          ))}
          {hasOverride() && (
            <button role="menuitem" onClick={useDefault}
              style={{
                display: "block", width: "100%", textAlign: "start", padding: "7px 10px",
                marginTop: 4, borderTop: "1px solid var(--border)", border: 0,
                background: "none", color: "var(--fg-faint)", cursor: "pointer", fontSize: 12,
              }}>
              {t("app.useTenantDefault")}
            </button>
          )}
        </div>
      )}
    </span>
  );
}

export default LocaleSwitcher;
